/**
 * ErrorModal — a small, reusable "something went wrong" dialog. Body-level
 * singleton; call `errorModal.show(title, message)` from anywhere (sign-in
 * failures, server errors, etc.) so failures surface to the player instead of
 * dying silently in the console.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

interface ErrorModalEvents {
  'dismiss': void;
}

const STYLE_ID = 'sdl-error-styles';

export class ErrorModal extends EventEmitter<ErrorModalEvents> {
  public element: HTMLElement;
  private titleEl: HTMLElement;
  private msgEl: HTMLElement;
  private visible = false;

  constructor() {
    super();
    ErrorModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-error-host';
    this.element.innerHTML = `
      <div class="sdl-error-backdrop"></div>
      <div class="sdl-error-panel">
        <div class="sdl-error-icon">!</div>
        <div class="sdl-error-title"></div>
        <div class="sdl-error-msg"></div>
        <button class="sdl-error-btn" data-ok>GOT IT</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.titleEl = this.element.querySelector('.sdl-error-title') as HTMLElement;
    this.msgEl = this.element.querySelector('.sdl-error-msg') as HTMLElement;

    this.element.querySelector('[data-ok]')!.addEventListener('click', () => this.dismiss());
    this.element.querySelector('.sdl-error-backdrop')!.addEventListener('click', () => this.dismiss());
  }

  show(title = 'Something went wrong', message = 'Please try again.'): void {
    this.titleEl.textContent = title;
    this.msgEl.textContent = message;
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
  }

  isVisible(): boolean {
    return this.visible;
  }

  private dismiss(): void {
    this.emit('dismiss', undefined);
    this.hide();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-error-host {
        position: fixed; inset: 0;
        z-index: 900;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-error-host.visible { display: flex; }
      .sdl-error-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75);
        backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px);
      }
      .sdl-error-panel {
        position: relative;
        width: 290px;
        padding: 26px 22px 18px;
        background: var(--concrete, #111114);
        border: 1px solid rgba(255,51,102,0.4);
        border-radius: 20px;
        display: flex; flex-direction: column; align-items: center;
        gap: 8px;
        box-shadow: 0 18px 50px rgba(0,0,0,0.6);
        animation: sdl-error-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes sdl-error-in {
        0% { opacity: 0; transform: scale(0.8); }
        100% { opacity: 1; transform: scale(1); }
      }
      .sdl-error-icon {
        width: 48px; height: 48px;
        border-radius: 50%;
        background: rgba(255,51,102,0.15);
        border: 2px solid #FF3366;
        color: #FF3366;
        display: flex; align-items: center; justify-content: center;
        font-family: 'Bebas Neue', sans-serif; font-size: 30px;
        margin-bottom: 4px;
      }
      .sdl-error-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 24px; letter-spacing: 1px; text-align: center;
        color: #fff;
      }
      .sdl-error-msg {
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; line-height: 1.4; text-align: center;
        color: rgba(255,255,255,0.6);
        margin-bottom: 8px;
      }
      .sdl-error-btn {
        width: 100%;
        padding: 13px 0;
        border: none; border-radius: 13px;
        background: rgba(255,255,255,0.1);
        color: #fff;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px; letter-spacing: 2px;
        cursor: pointer;
        transition: transform 0.1s, background 0.15s;
      }
      .sdl-error-btn:active { transform: scale(0.97); background: rgba(255,255,255,0.16); }
    `;
    document.head.appendChild(style);
  }
}
